import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";

export default function GalleryImageViewer({
  open,
  images,
  currentIndex,
  onClose,
  onChangeIndex,
  galleryName,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const currentImage = useMemo(() => {
    if (!Array.isArray(images) || !images.length) return null;
    return images[currentIndex] || null;
  }, [images, currentIndex]);

  useEffect(() => {
    if (!open) return;
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setDragging(false);
  }, [open, currentIndex]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }

      if (event.key === "ArrowRight" && currentIndex < images.length - 1) {
        onChangeIndex(currentIndex + 1);
      }

      if (event.key === "ArrowLeft" && currentIndex > 0) {
        onChangeIndex(currentIndex - 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose, onChangeIndex, currentIndex, images.length]);

  const handlePrevious = () => {
    if (currentIndex <= 0) return;
    onChangeIndex(currentIndex - 1);
  };

  const handleNext = () => {
    if (currentIndex >= images.length - 1) return;
    onChangeIndex(currentIndex + 1);
  };

  const increaseZoom = () => {
    setZoom((prev) => Math.min(prev + 0.25, 4));
  };

  const decreaseZoom = () => {
    setZoom((prev) => {
      const next = Math.max(prev - 0.25, 1);
      if (next === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return next;
    });
  };

  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (event) => {
    if (zoom <= 1 || isMobile) return;
    setDragging(true);
    setDragStart({
      x: event.clientX - position.x,
      y: event.clientY - position.y,
    });
  };

  const handleMouseMove = (event) => {
    if (!dragging || zoom <= 1 || isMobile) return;
    setPosition({
      x: event.clientX - dragStart.x,
      y: event.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setDragging(false);
  };

  const handleMouseLeave = () => {
    setDragging(false);
  };

  if (!currentImage) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      maxWidth={false}
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          overflow: "hidden",
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(7,10,17,0.82)",
          backdropFilter: "blur(3px)",
        },
      }}
    >
      <Box
        sx={{
          width: "100vw",
          height: "100vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 5,
            px: { xs: 1, sm: 2 },
            py: 1.2,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.18) 100%)",
          }}
        >
          <Stack
            direction={{ xs: "row", md: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack spacing={0.3} sx={{ minWidth: 0, pr: 1 }}>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: { xs: "0.92rem", sm: "1rem" },
                  fontWeight: 800,
                  lineHeight: 1.2,
                }}
              >
                {galleryName || "Vista previa"}
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.82)",
                  fontSize: { xs: "0.8rem", sm: "0.88rem" },
                  fontWeight: 600,
                }}
              >
                Image {currentIndex + 1} de {images.length}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={0.8} alignItems="center">
              {!isMobile && (
                <>
                  <Button
                    variant="contained"
                    startIcon={<ZoomOutRoundedIcon sx={{ fontSize: 22 }} />}
                    onClick={decreaseZoom}
                    sx={{
                      minHeight: 42,
                      borderRadius: "12px",
                      backgroundColor: "rgba(255,255,255,0.16)",
                      color: "#fff",
                      backdropFilter: "blur(4px)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.24)",
                      },
                    }}
                  >
                    Zoom -
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<RestartAltRoundedIcon sx={{ fontSize: 22 }} />}
                    onClick={resetZoom}
                    sx={{
                      minHeight: 42,
                      borderRadius: "12px",
                      backgroundColor: "rgba(255,255,255,0.16)",
                      color: "#fff",
                      backdropFilter: "blur(4px)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.24)",
                      },
                    }}
                  >
                    Reset
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<ZoomInRoundedIcon sx={{ fontSize: 22 }} />}
                    onClick={increaseZoom}
                    sx={{
                      minHeight: 42,
                      borderRadius: "12px",
                      backgroundColor: "rgba(255,255,255,0.16)",
                      color: "#fff",
                      backdropFilter: "blur(4px)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.24)",
                      },
                    }}
                  >
                    Zoom +
                  </Button>
                </>
              )}

              <IconButton
                onClick={onClose}
                sx={{
                  color: "#fff",
                  backgroundColor: "rgba(255,255,255,0.16)",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.24)",
                  },
                }}
              >
                <CloseRoundedIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        <IconButton
          onClick={handlePrevious}
          disabled={currentIndex === 0}
          sx={{
            position: "absolute",
            left: { xs: 8, sm: 18 },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 6,
            color: "#fff",
            backgroundColor:
              currentIndex === 0
                ? "rgba(255,255,255,0.08)"
                : "rgba(255,255,255,0.14)",
            "&:hover": {
              backgroundColor:
                currentIndex === 0
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(255,255,255,0.22)",
            },
          }}
        >
          <NavigateBeforeRoundedIcon sx={{ fontSize: 34 }} />
        </IconButton>

        <IconButton
          onClick={handleNext}
          disabled={currentIndex === images.length - 1}
          sx={{
            position: "absolute",
            right: { xs: 8, sm: 18 },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 6,
            color: "#fff",
            backgroundColor:
              currentIndex === images.length - 1
                ? "rgba(255,255,255,0.08)"
                : "rgba(255,255,255,0.14)",
            "&:hover": {
              backgroundColor:
                currentIndex === images.length - 1
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(255,255,255,0.22)",
            },
          }}
        >
          <NavigateNextRoundedIcon sx={{ fontSize: 34 }} />
        </IconButton>

        <Box
          sx={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2, sm: 8, md: 12 },
            py: { xs: 10, sm: 12 },
            cursor: !isMobile && zoom > 1 ? (dragging ? "grabbing" : "grab") : "default",
            userSelect: "none",
            touchAction: isMobile ? "pinch-zoom" : "none",
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <Box
            onMouseDown={handleMouseDown}
            sx={{
              transform: !isMobile ? `translate(${position.x}px, ${position.y}px)` : "translate(0px, 0px)",
              transition: dragging ? "none" : "transform 0.18s ease",
              display: "inline-flex",
              maxWidth: "100%",
              maxHeight: "100%",
            }}
          >
            <Box
              component="img"
              src={currentImage.image}
              alt=""
              draggable={false}
              sx={{
                maxWidth: isMobile ? "82vw" : "100%",
                maxHeight: isMobile ? "72vh" : "100%",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                transform: !isMobile ? `scale(${zoom})` : "scale(1)",
                transformOrigin: "center center",
                transition: dragging ? "none" : "transform 0.18s ease",
                display: "block",
              }}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}